/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import api, { usersAPI, agencyAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
    Users,
    Plus,
    Mail,
    Phone,
    MoreVertical,
    UserPlus,
    UserMinus,
    UserX,
    Loader2,
    Sparkles
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

// Animation variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
};

function TeamPage() {
    const [team, setTeam] = useState([]);
    const [agency, setAgency] = useState(null);
    const [loading, setLoading] = useState(true);
    const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [inviteData, setInviteData] = useState({
        email: '',
        first_name: '',
        last_name: '',
        phone: ''
    });

    const loadData = useCallback(async () => {
        // Use allSettled so a single endpoint failure doesn't blank the page or fire
        // a scary toast. We log silently and degrade gracefully (empty list, no agency name).
        const [teamRes, agencyRes] = await Promise.allSettled([
            usersAPI.getTeam(),
            agencyAPI.getMyAgency(),
        ]);
        if (teamRes.status === 'fulfilled') {
            setTeam(Array.isArray(teamRes.value.data) ? teamRes.value.data : []);
        } else {
            console.warn('Team load failed', teamRes.reason);
            setTeam([]);
        }
        if (agencyRes.status === 'fulfilled') {
            setAgency(agencyRes.value.data);
        } else {
            console.warn('Agency load failed', agencyRes.reason);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const inviteAgent = async () => {
        if (!inviteData.email || !inviteData.first_name || !inviteData.last_name) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }

        setInviting(true);
        try {
            await usersAPI.inviteAgent(inviteData);
            toast.success('Invitation envoyée par email !');
            setInviteDialogOpen(false);
            setInviteData({ email: '', first_name: '', last_name: '', phone: '' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
        } finally {
            setInviting(false);
        }
    };

    const toggleUserActive = async (userId) => {
        try {
            await usersAPI.toggleUserActive(userId);
            toast.success('Statut mis à jour');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };

    const detachAgent = async (userId, name) => {
        if (!window.confirm(`Détacher ${name} de l'agence ?\n\nSon HomlyScore et ses certifications seront conservés.\nSes mandats en cours seront réassignés à l'admin.`)) return;
        try {
            await api.post(`/users/${userId}/detach`);
            toast.success(`${name} a été détaché. Un email lui a été envoyé.`);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors du détachement');
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-12 w-64 bg-muted" />
                <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                        <Skeleton key={i} className="h-24 w-full bg-muted" />
                    ))}
                </div>
            </div>
        );
    }

    const admins = team.filter(m => m.role === 'admin');
    const agents = team.filter(m => m.role === 'agent');

    return (
        <motion.div 
            className="space-y-8" 
            data-testid="team-page"
            initial="hidden"
            animate="visible"
            variants={stagger}
        >
            {/* Header */}
            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <Users className="w-7 h-7 text-[#FF914D]" />
                        <h1 className="text-3xl font-black text-foreground">Équipe</h1>
                    </div>
                    <p className="text-muted-foreground">
                        {team.length} membre(s){agency?.name ? ` dans ${agency.name}` : ''}
                    </p>
                </div>
                <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                    <DialogTrigger asChild>
                        <Button 
                            className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white shadow-lg shadow-[#681793]/30"
                            data-testid="invite-agent-button"
                        >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Inviter un agent
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border text-foreground">
                        <DialogHeader>
                            <DialogTitle>Inviter un agent</DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                                L'agent recevra un email pour créer son compte.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="first_name" className="text-muted-foreground">Prénom *</Label>
                                    <Input
                                        id="first_name"
                                        value={inviteData.first_name}
                                        onChange={(e) => setInviteData({ ...inviteData, first_name: e.target.value })}
                                        placeholder="Jean"
                                        className="bg-card border-border text-foreground"
                                        data-testid="invite-firstname-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="last_name" className="text-muted-foreground">Nom *</Label>
                                    <Input
                                        id="last_name"
                                        value={inviteData.last_name}
                                        onChange={(e) => setInviteData({ ...inviteData, last_name: e.target.value })}
                                        placeholder="Dupont"
                                        className="bg-card border-border text-foreground"
                                        data-testid="invite-lastname-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="email" className="text-muted-foreground">Email *</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={inviteData.email}
                                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                                    placeholder="jean@agence.fr"
                                    className="bg-card border-border text-white"
                                    data-testid="invite-email-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="phone" className="text-muted-foreground">Téléphone</Label>
                                <Input
                                    id="phone"
                                    value={inviteData.phone}
                                    onChange={(e) => setInviteData({ ...inviteData, phone: e.target.value })}
                                    placeholder="06 12 34 56 78"
                                    className="bg-card border-border text-white"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button 
                                variant="outline" 
                                onClick={() => setInviteDialogOpen(false)}
                                className="border-border text-muted-foreground hover:text-foreground hover:bg-card"
                            >
                                Annuler
                            </Button>
                            <Button 
                                onClick={inviteAgent} 
                                disabled={inviting}
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90"
                                data-testid="send-invite-button"
                            >
                                {inviting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Envoi...
                                    </>
                                ) : (
                                    'Envoyer l\'invitation'
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </motion.div>

            {/* Admins */}
            <motion.div variants={fadeIn}>
                <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                    <div className="p-5 border-b border-border">
                        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                            <Users className="w-5 h-5 text-[#681793]" />
                            Administration ({admins.length})
                        </h2>
                    </div>
                    <div className="p-5">
                        <div className="space-y-3">
                            {admins.map((member) => (
                                <div key={member.id} className="flex items-center justify-between p-4 rounded-xl bg-card border border-border">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-bold">
                                            {member.first_name[0]}{member.last_name[0]}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-semibold text-foreground">{member.first_name} {member.last_name}</p>
                                                <Badge className="bg-[#681793]/20 text-[#681793] border-0">Admin</Badge>
                                            </div>
                                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                    <Mail className="w-3 h-3" />
                                                    {member.email}
                                                </span>
                                                {member.phone && (
                                                    <span className="flex items-center gap-1">
                                                        <Phone className="w-3 h-3" />
                                                        {member.phone}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Agents */}
            <motion.div variants={fadeIn}>
                <div className="bg-card backdrop-blur-sm rounded-2xl border border-border overflow-hidden">
                    <div className="p-5 border-b border-border">
                        <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
                            <Users className="w-5 h-5 text-[#FF914D]" />
                            Agents commerciaux ({agents.length})
                        </h2>
                    </div>
                    <div className="p-5">
                        {agents.length === 0 ? (
                            <div className="text-center py-8">
                                <div className="w-14 h-14 rounded-2xl bg-card flex items-center justify-center mx-auto mb-4">
                                    <Users className="w-7 h-7 text-muted-foreground" />
                                </div>
                                <p className="text-muted-foreground mb-4">Aucun agent dans l'équipe</p>
                                <Button 
                                    onClick={() => setInviteDialogOpen(true)}
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Inviter un agent
                                </Button>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {agents.map((member, idx) => (
                                    <motion.div 
                                        key={member.id} 
                                        className="flex items-center justify-between p-4 rounded-xl bg-card border border-border hover:border-border transition-all"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: idx * 0.05 }}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold ${member.is_active ? 'bg-gradient-to-br from-[#681793] to-[#FF914D] text-white' : 'bg-muted text-muted-foreground'}`}>
                                                {member.first_name[0]}{member.last_name[0]}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="font-semibold text-foreground">{member.first_name} {member.last_name}</p>
                                                    {!member.is_active && (
                                                        <Badge variant="outline" className="border-white/20 text-muted-foreground">Inactif</Badge>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                                    <span className="flex items-center gap-1">
                                                        <Mail className="w-3 h-3" />
                                                        {member.email}
                                                    </span>
                                                    {member.phone && (
                                                        <span className="flex items-center gap-1">
                                                            <Phone className="w-3 h-3" />
                                                            {member.phone}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Inscrit le {formatDate(member.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground hover:bg-card">
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="bg-slate-900 border-border">
                                                <DropdownMenuItem 
                                                    onClick={() => toggleUserActive(member.id)}
                                                    className="text-muted-foreground hover:text-foreground hover:bg-card focus:bg-card"
                                                >
                                                    {member.is_active ? (
                                                        <>
                                                            <UserMinus className="w-4 h-4 mr-2" />
                                                            Désactiver
                                                        </>
                                                    ) : (
                                                        <>
                                                            <UserPlus className="w-4 h-4 mr-2" />
                                                            Activer
                                                        </>
                                                    )}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem 
                                                    onClick={() => detachAgent(member.id, `${member.first_name} ${member.last_name}`)}
                                                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 focus:bg-red-500/10"
                                                >
                                                    <UserX className="w-4 h-4 mr-2" />
                                                    Détacher de l'agence
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
}

export default TeamPage;
